import React, {Fragment, useReducer, useRef} from 'react'

import _ from 'underscore'

import LoginActions from '../../../../actions/LoginActions.js'
import reducer from '../../../../helpers/reducer.js'

import {Alert, IconButton, H3, H5, P, Info, Button, Toast, Colors} from '../../../../components/UI/index.js'
import {formSpacing, FormRow, FormStack, MetricInput, Toggle, Input, Select} from '../../../../components/UI/forms/index.js'
import t from '../../../../helpers/Languages.js'
import {Customer} from '../../../../../server/functions/customers/customers.types.js'
import {Reseller} from '../../../../../server/functions/resellers/resellers.types.js'

interface WooCommerceSettingsProps {
    user: Customer
    onChange: (user: Customer, save?: boolean) => void
    reseller: Reseller
}

interface WooCommerceSettingsState {
    loading: boolean
    verified: boolean
    error: string
    shippingmethodsLoading: boolean
}

const initialState = {
    loading: false,
    verified: false,
    error: '',
    shippingmethodsLoading: false
}

export default function WooCommerceSettings({user, onChange, reseller}: WooCommerceSettingsProps) {
    const [{loading, verified, error, shippingmethodsLoading}, dispatch] = useReducer<React.Reducer<WooCommerceSettingsState, Partial<WooCommerceSettingsState>>>(reducer, initialState)
    const toastRef = useRef(null)

    function onChangeChecked(key, event) {
        user.settings.webshops.woocommerce[key] = event.target.checked

        onChange(user)
    }

    function onChangeValue(key: string, event: React.ChangeEvent<HTMLInputElement>, parser?: (value: any) => any) {
        if (typeof parser === 'function') {
            user.settings.webshops.woocommerce[key] = parser(event.target.value)
        } else {
            user.settings.webshops.woocommerce[key] = event.target.value
        }

        onChange(user)
    }

    function onChangeValues(key: string, event: any) {
        user.settings.webshops.woocommerce[key] = event.target.values

        onChange(user)
    }

    function onVerifyConnection() {
        dispatch({loading: true, verified: false, error: ''})

        onChange(user, true)

        setTimeout(() => { // wait for saving
            LoginActions.verifyWebshop('woocommerce', (err) => {
                if (err) {
                    dispatch({loading: false, error: `Verificatie niet succesvol, controleer de ingevulde gegevens. Foutmelding: ${err}`})
                } else {
                    dispatch({loading: false, verified: true})
                }
            })
        }, 1000)
    }

    function onClickGetShippingMethods() {
        dispatch({shippingmethodsLoading: true, error: ''})

        LoginActions.getShippingMethods(user, (err, shippingMethods) => {
            if (!err) {
                shippingMethods = shippingMethods.woocommerce || []

                const oldShippingMethods = JSON.parse(JSON.stringify(user.settings.webshops.woocommerce.shippingMethods || []))
                user.settings.webshops.woocommerce.shippingMethods = []

                shippingMethods.map((shippingMethod) => {
                    const oldShippingmethod = _.findWhere(oldShippingMethods, {id: shippingMethod.id})

                    shippingMethod.disabled = oldShippingmethod ? oldShippingmethod.disabled : false
                    shippingMethod.options = oldShippingmethod ? oldShippingmethod.options : []

                    shippingMethod.shippingClasses.map((shippingClass) => {
                        shippingClass.disabled = oldShippingmethod ? _.findWhere(oldShippingmethod.shippingClasses, {id: shippingClass.id})?.disabled || false : false
                        shippingClass.options = oldShippingmethod ? _.findWhere(oldShippingmethod.shippingClasses, {id: shippingClass.id})?.options || [] : []

                        return shippingClass
                    })

                    user.settings.webshops.woocommerce.shippingMethods.push(shippingMethod)
                })

                onChange(user)
            }

            dispatch({shippingmethodsLoading: false, error: err})
        })
    }

    function onChangeShippingMethodDisabled(index) {
        user.settings.webshops.woocommerce.shippingMethods[index].disabled = !user.settings.webshops.woocommerce.shippingMethods[index].disabled

        onChange(user)
    }

    function onChangeShippingClassDisabled(shippingMethodIndex, shippingClassIndex) {
        if (user.settings.webshops.woocommerce.shippingMethods[shippingMethodIndex].shippingClasses?.[shippingClassIndex]) {
            user.settings.webshops.woocommerce.shippingMethods[shippingMethodIndex].shippingClasses[shippingClassIndex].disabled = !user.settings.webshops.woocommerce.shippingMethods[shippingMethodIndex].shippingClasses[shippingClassIndex].disabled

            onChange(user)
        }
    }

    function onChangeShippingClass(shippingMethodIndex, shippingClassIndex, event) {
        if (user.settings.webshops.woocommerce.shippingMethods[shippingMethodIndex].shippingClasses?.[shippingClassIndex]) {
            user.settings.webshops.woocommerce.shippingMethods[shippingMethodIndex].shippingClasses[shippingClassIndex].options = event.target.values

            onChange(user)
        }
    }

    function onChangeCheckedParcel(key, event) {
        user.settings.parcels[key] = event.target.checked

        onChange(user)
    }

    function onChangeValueParcel(key, event) {
        user.settings.parcels[key] = event.target.value

        onChange(user)
    }

    const options = (_.keys(reseller.settings.parcels.products) || []).filter((product) => {
        return _.some(_.keys(reseller.settings.parcels.products[product]), (carrier) => {
            return reseller.settings.parcels.products[product][carrier]
        })
    }).sort()

    const enabledOptions = options.map((option) => {
        return {value: option, title: option}
    })

    return (
        <div>
            <H3 style={{marginBottom: 36}}>WooCommerce</H3>

            <H5 style={{marginBottom: 24}}>Instellingen</H5>

            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                <Toggle
                    label='Actief'
                    checked={user.settings.webshops.woocommerce.active}
                    onChange={(event) => onChangeChecked('active', event)}
                />
            </FormStack>

            {user.settings.webshops.woocommerce.active &&
                <>
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Input
                            label='Webshop URL'
                            value={user.settings.webshops.woocommerce.url}
                            onChange={(event) => onChangeValue('url', event)}
                        />

                        <Input
                            label='Consumer key'
                            info='Deze kun je aanmaken in je WooCommerce omgeving.'
                            value={user.settings.webshops.woocommerce.apiKey}
                            onChange={(event) => onChangeValue('apiKey', event)}
                        />

                        <Input
                            label='Consumer secret'
                            info='Deze kun je aanmaken in je WooCommerce omgeving.'
                            value={user.settings.webshops.woocommerce.apiSecret}
                            onChange={(event) => onChangeValue('apiSecret', event)}
                        />

                        <Input
                            type='number'
                            min={1}
                            max={365}
                            label='Aantal dagen terugkijken voor ophalen bestellingen'
                            value={user.settings.webshops.woocommerce.nrOfDays || 7}
                            onChange={(event) => onChangeValue('nrOfDays', event, parseInt)}
                        />

                        <Select
                            label='Update bestelling status in WooCommerce'
                            info=''
                            value={user.settings.webshops.woocommerce.updateStatus}
                            onChange={(event) => onChangeValue('updateStatus', event)}
                        >
                            <option value=''>Nooit</option>
                            <option value='confirm'>Na bevestigen</option>
                            <option value='afterSent'>Na verzenden</option>
                        </Select>

                        <Select
                            label='Importeer bestellingen met status'
                            info='Selecteer de statussen waarvoor bestellingen worden geïmporteerd'
                            options={[
                                {value: 'pending', title: 'Pending'},
                                {value: 'on-hold', title: 'On-hold'},
                                {value: 'processing', title: 'Processing'},
                                {value: 'completed', title: 'Completed'}
                            ]}
                            allowCustomValues
                            values={user.settings.webshops.woocommerce.statuses || []}
                            onChange={(event) => onChangeValues('statuses', event)}
                        />

                        <Toggle
                            label='Gewicht in gram'
                            info='WooCommerce gebruikt standaard kilogram als gewichtseenheid. Een plugin kan dit aanpassen naar gram, zet in dat geval deze optie aan.'
                            checked={user.settings.webshops.woocommerce.weightInGrams}
                            onChange={(event) => onChangeChecked('weightInGrams', event)}
                        />
                    </FormStack>

                    <br />
                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Input
                            label='Straat veld'
                            info='Als gebruik wordt gemaakt van een plugin in WooCommerce die de naam van het straat veld aanpast kan deze hier ingevuld worden.'
                            value={user.settings.webshops.woocommerce.streetField}
                            onChange={(event) => onChangeValue('streetField', event)}
                        />

                        <Input
                            label='Huisnummer veld'
                            info='Als gebruik wordt gemaakt van een plugin in WooCommerce die de naam van het huisnummer veld aanpast kan deze hier ingevuld worden.'
                            value={user.settings.webshops.woocommerce.nrField}
                            onChange={(event) => onChangeValue('nrField', event)}
                        />

                        <Input
                            label='Huisnummer toevoeging veld'
                            info='Als gebruik wordt gemaakt van een plugin in WooCommerce die de naam van het huisnummertoevoeging veld aanpast kan deze hier ingevuld worden.'
                            value={user.settings.webshops.woocommerce.additionField}
                            onChange={(event) => onChangeValue('additionField', event)}
                        />

                        <Toggle
                            label='Consument opmerking meegeven als bezorginstructie'
                            checked={user.settings.webshops.woocommerce.useComment}
                            onChange={(event) => onChangeChecked('useComment', event)}
                        />
                    </FormStack>

                    <H5 style={{marginBottom: 24, marginTop: 36}}>{t('webhookUrl')}</H5>

                    <P>{t('webhookUrlExplanation')} {t('seeThe')} <a target='_blank' href='https://handleiding.veloyd.nl/docs/woocommerce/' rel='noreferrer'>{t('manualLowercase')}</a> {t('forMoreInfoOnWebhooks')}</P>

                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <pre style={{color: Colors.textDark}}>{`https://${window.location.host}/api/parcel/woocommerce?customer_id=${user._id}`}</pre>
                        <IconButton
                            onClick={() => {
                                navigator.clipboard.writeText(`https://${window.location.host}/api/parcel/woocommerce?customer_id=${user._id}`).then(() => {
                                    toastRef.current?.showToast('success', 'Link gekopieerd', 2500)
                                })
                            }}
                            icon='mdi mdi-content-copy'
                        />
                    </div>

                    <FormStack style={{marginTop: 12}}>
                        <Input
                            label={t('webhookSecret')}
                            info={t('webhookSecretInfo')}
                            type='password'
                            autoComplete='new-password'
                            value={user.settings.webshops.woocommerce.webhookSecret || ''}
                            onChange={(event) => onChangeValue('webhookSecret', event)}
                        />
                    </FormStack>

                    <H5 style={{marginBottom: 24, marginTop: 36}}>Verzendmethodes</H5>

                    <div style={{display: 'flex', alignItems: 'center', marginBottom: 12}}>
                        <P style={{display: 'flex', flex: 1}}>Verzendmethodes ophalen uit WooCommerce
                            <Info style={{marginLeft: 6}} text='Klik op de knop om verzendmethodes uit WooCommerce op te halen. Selecteer vervolgens voor elke verzendmethode de nodige verzendopties.' />
                        </P>

                        <IconButton loading={shippingmethodsLoading} onClick={onClickGetShippingMethods}>
                            <i className='mdi mdi-refresh' />
                        </IconButton>
                    </div>

                    {(user.settings.webshops.woocommerce.shippingMethods || []).map((shippingmethod, index) => {
                        return (
                            <Fragment key={`shippingmethod${index}`}>
                                <div>
                                    <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                        <Toggle
                                            checked={!shippingmethod.disabled}
                                            onChange={() => onChangeShippingMethodDisabled(index)}
                                        />

                                        <P style={{marginLeft: 14, flex: 1, fontWeight: 700}}>{shippingmethod.name}</P>

                                        <div style={{flex: 1}} />
                                    </div>

                                    {!shippingmethod.disabled &&
                                        <div>
                                            {shippingmethod.shippingClasses?.map((shippingClass, shippingClassIndex) => (
                                                <div key={`shippingClass${shippingClassIndex}`} style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                                    <Toggle
                                                        style={{marginLeft: 24}}
                                                        checked={!shippingClass.disabled}
                                                        onChange={() => onChangeShippingClassDisabled(index, shippingClassIndex)}
                                                    />

                                                    <P style={{flex: 1}}>{shippingClass.name}</P>

                                                    <Select
                                                        noSort
                                                        label='Verzendopties'
                                                        values={shippingClass.options || []}
                                                        options={enabledOptions}
                                                        onChange={(event) => onChangeShippingClass(index, shippingClassIndex, event)}
                                                        disabled={shippingClass.disabled}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    }
                                </div>
                            </Fragment>
                        )
                    })}

                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        <Toggle
                            label='Automatisch brievenbuspakket'
                            info='Zendingen die aan de ingestelde voorwaarden voldoen worden automatisch aangemeld als brievenbuspakket.'
                            checked={!!user.settings.parcels.autoMailboxParcel}
                            onChange={(event) => onChangeCheckedParcel('autoMailboxParcel', event)}
                        />

                        {user.settings.parcels.autoMailboxParcel &&
                            <>
                                <FormRow>
                                    <Toggle
                                        label='Maximaal gewicht'
                                        checked={!!user.settings.parcels.useMailboxParcelWeight}
                                        onChange={(event) => onChangeCheckedParcel('useMailboxParcelWeight', event)}
                                    />

                                    <MetricInput
                                        label='Gewicht'
                                        value={user.settings.parcels.mailboxParcelWeight}
                                        onChange={(event) => onChangeValueParcel('mailboxParcelWeight', event)}
                                        placeholder={2000}
                                        max={2000}
                                        append='g'
                                        disabled={!user.settings.parcels.useMailboxParcelWeight}
                                    />
                                </FormRow>

                                <FormRow>
                                    <Toggle
                                        label='Maximale waarde'
                                        checked={!!user.settings.parcels.useMailboxParcelValue}
                                        onChange={(event) => onChangeCheckedParcel('useMailboxParcelValue', event)}
                                    />

                                    <MetricInput
                                        label='Waarde'
                                        value={user.settings.parcels.mailboxParcelValue}
                                        onChange={(event) => onChangeValueParcel('mailboxParcelValue', event)}
                                        divisionFactor={100}
                                        prepend='€'
                                        disabled={!user.settings.parcels.useMailboxParcelValue}
                                    />
                                </FormRow>
                            </>
                        }
                    </FormStack>

                    <FormStack>
                        <Button
                            variant='text'
                            loading={loading}
                            onClick={onVerifyConnection}
                        >
                            Verifieer
                        </Button>

                        {error && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({error: ''})} variant='error'>{error}</Alert>
                        )}

                        {!error && verified && (
                            <Alert style={{marginBottom: 6}} onClose={() => dispatch({verified: false})}>Verificatie succesvol.</Alert>
                        )}
                    </FormStack>
                </>
            }

            <Toast ref={toastRef} />
        </div>
    )
}
